import { TeContainer, TeContainerSlim, TeLabel, TeDate, TeToggle } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { endOfDay, startOfDay, max, min, parseISO } from 'date-fns';
import { TeDateChangeEvent } from '@topcon/tuicl';

import { CombinedUser } from '../types';
import { Filter, FilterIDs } from '../../../providers/filter/filter.types';
import { useFilters } from '../hooks';
import FilterContainer from '../FilterContainer';
import ProfileSelector from '../../../components/ProfileSelector';

interface Props {
  users?: CombinedUser[];
}

function UserFilters({ users }: Props) {
  const { t } = useTranslation('users');
  const filterID = FilterIDs.UserFilters;
  const filterData = useFilters(filterID);
  const { filtersCopy, handleChange } = filterData;

  const selectedStatuses = (filtersCopy?.find(({ name }) => name === 'status')?.value ??
    []) as string[];

  const selectedProfiles = useMemo(() => {
    return (filtersCopy?.find(({ name }) => name === 'profile')?.value ?? []) as string[];
  }, [filtersCopy]);

  const onSetProfiles = (nextSelected: string[]) => {
    if (
      nextSelected.length === selectedProfiles.length &&
      nextSelected.every((p) => selectedProfiles.includes(p))
    ) {
      return;
    }
    handleChange(new Filter(filterID, 'profile', t('profile'), nextSelected, true));
  };

  const [startDate, endDate] = ['addedFrom', 'addedTo']
    .map((field) => filtersCopy?.find(({ name }) => name === field)?.value as string | undefined)
    .map((d) => (d ? new Date(d) : undefined));
  const today = new Date();

  const minCreatedAt = useMemo(() => {
    if (!users || !users.length) return;
    return startOfDay(
      min(
        users.map(({ createdAt }) => {
          if (typeof createdAt === 'string') {
            return parseISO(createdAt);
          }
          return createdAt;
        })
      )
    );
  }, [users]);

  const maxCreatedAt = useMemo(() => {
    if (!users || !users.length) return;
    return endOfDay(
      max(
        users.map(({ createdAt }) => {
          if (typeof createdAt === 'string') {
            return parseISO(createdAt);
          }
          return createdAt;
        })
      )
    );
  }, [users]);

  const minStart = minCreatedAt;
  const maxStart = endDate || maxCreatedAt || today;
  const minEnd = startDate || minCreatedAt;
  const maxEnd = maxCreatedAt || today;

  const onStatusToggleChange = (value: boolean, opt: string) => {
    const statuses = selectedStatuses.filter((s) => s !== opt || (s === opt && value));
    if (value) statuses.push(opt);
    return handleChange(new Filter(filterID, 'status', t('status'), statuses, true));
  };

  const onStartChange = (e: TeDateChangeEvent) => {
    const d = e.date;
    handleChange(
      new Filter(
        filterID,
        'addedFrom',
        t('addedFrom'),
        d ? startOfDay(d).toISOString() : '',
        true,
        'date'
      )
    );
  };

  const onEndChange = (e: TeDateChangeEvent) => {
    const d = e.date;
    handleChange(
      new Filter(
        filterID,
        'addedTo',
        t('addedTo'),
        d ? endOfDay(d).toISOString() : '',
        true,
        'date'
      )
    );
  };

  return (
    <FilterContainer
      filterData={filterData}
      title={t('filters.title')}
      description={t('filters.description')}
    >
      <TeContainer padding={['l', 'm']} gap="l" column grow overflow="auto">
        <TeContainerSlim gap="xs" column fill>
          <TeLabel text={t('filters.statusLabel')} />
          <TeContainerSlim gap="xs" column fill>
            <TeToggle
              label={t('active')}
              size="s"
              toggled={selectedStatuses.includes('active')}
              tetoggle={(event) => onStatusToggleChange(event.toggled, 'active')}
            />
            <TeToggle
              label={t('invited')}
              size="s"
              toggled={selectedStatuses.includes('invited')}
              tetoggle={(event) => onStatusToggleChange(event.toggled, 'invited')}
            />
          </TeContainerSlim>
        </TeContainerSlim>
        <TeContainerSlim gap="xs" column fill>
          <TeLabel text={t('filters.dateAdded')} />
          <TeContainerSlim gap="xs" fill>
            <TeDate
              date={startDate}
              minDate={minStart}
              maxDate={maxStart}
              techange={onStartChange}
              size="s"
              variant="compact"
            />
            <TeDate
              date={endDate}
              minDate={minEnd}
              maxDate={maxEnd}
              techange={onEndChange}
              size="s"
              variant="compact"
            />
          </TeContainerSlim>
        </TeContainerSlim>
        <TeContainerSlim gap="xs" column fill overflow="auto">
          <TeLabel text={t('filters.profileLabel')} />
          <ProfileSelector profiles={selectedProfiles} setProfiles={onSetProfiles} />
        </TeContainerSlim>
      </TeContainer>
    </FilterContainer>
  );
}

export default UserFilters;
